import {
    Coordinate,
    Coordinates,
    DirectionsAPIRoute,
    MapDirectionsDuration,
    MapDirectionsSetting,
} from '@bpd-library/types';
import { calculateBoundingBox, environmentDetails } from '@bpd-library/utilities';
import mapboxgl, { Popup } from 'mapbox-gl';

interface DirectionsAPIResult {
    routes: DirectionsAPIRoute[];
}

const DIRECTIONS_SOURCE = 'directions';

let startPopup: Popup | undefined;
let endPopup: Popup | undefined;
let durationPopup: Popup | undefined;

const addDirections = async (map: mapboxgl.Map, directionsOptions: MapDirectionsSetting) => {
    await init();

    async function init() {
        startPopup?.remove();
        endPopup?.remove();
        durationPopup?.remove();

        if (!map.getSource(DIRECTIONS_SOURCE)) {
            setupSource();
            setupLayer();
        }

        const { profile, start, end, startLabel, endLabel, durationLabel, onInitialized } =
            directionsOptions;

        const accessToken = environmentDetails.mapboxAccessToken || '';

        try {
            const url = `https://api.mapbox.com/directions/v5/mapbox/${profile}/${start[0]},${start[1]};${end[0]},${end[1]}?overview=full&geometries=geojson&access_token=${accessToken}`;
            const {
                routes: [route],
            } = (await fetch(url).then((r) => r.json())) as DirectionsAPIResult;

            if (route) {
                const { geometry, duration } = route;

                const source = map.getSource(DIRECTIONS_SOURCE) as
                    | mapboxgl.GeoJSONSource
                    | undefined;
                source?.setData({
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            geometry: geometry,
                            properties: null,
                        },
                    ],
                });

                const middlePoint = geometry.coordinates[
                    ~~(geometry.coordinates.length / 2)
                ] as Coordinate;

                // TODO: find better middle point - the one that lies on the line that passes between the start and end point
                // possibly use turf but definitely something like this:
                //
                // - for each coordinate on the route (except start and end)
                // -     calculate distance between start and end
                // -     divide (coord -> start) and (coord -> end)
                //
                // the point that has that division closest to 1 lies on that middle line and should be a good approximation

                const transformedDuration = transformDuration(duration);

                startPopup = addPopup(start, startLabel());
                endPopup = addPopup(end, endLabel());
                durationPopup = addPopup(middlePoint, durationLabel(transformedDuration));

                fitApiResultBounds(geometry);

                onInitialized && onInitialized();
            }
        } catch (e) {
            if (environmentDetails.isLocal) {
                console.error(e);
            }
        }
    }

    function transformDuration(seconds: number): MapDirectionsDuration {
        const totalHours = seconds / 60 / 60;
        const hours = Math.floor(totalHours);
        const minutes = Math.round((totalHours - hours) * 0.6 * 100);

        return {
            hours,
            minutes,
        };
    }

    function setupSource() {
        map.addSource(DIRECTIONS_SOURCE, {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [],
            },
        });
    }

    function setupLayer() {
        map.addLayer({
            id: 'directionsStroke',
            type: 'line',
            source: DIRECTIONS_SOURCE,
            layout: {},
            paint: {
                'line-color': '#32C1DE',
                'line-width': 4,
                ...directionsOptions.linePaintOptions,
            },
        });
    }

    // function getMiddlePoint(start: Coordinates, end: Coordinates, points: Position[]) {
    //     //
    // }

    function addPopup(location: Coordinate, labelNode: Node, options?: mapboxgl.PopupOptions) {
        return new Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 5,
            focusAfterOpen: false,
            ...options,
        })
            .setLngLat(location)
            .setDOMContent(labelNode)
            .addTo(map);
    }

    function fitApiResultBounds(geometry: GeoJSON.LineString) {
        const coordinates = [geometry.coordinates] as Coordinates;
        const boundingBox = calculateBoundingBox(coordinates);

        map.fitBounds(boundingBox, {
            padding: directionsOptions.boundsPadding || 50,
            animate: true,
            duration: 1000,
        });
    }
};

function removeDirections(map: mapboxgl.Map) {
    const source = map.getSource(DIRECTIONS_SOURCE) as mapboxgl.GeoJSONSource | undefined;

    if (!source) return;

    source.setData({
        type: 'FeatureCollection',
        features: [],
    });

    startPopup?.remove();
    endPopup?.remove();
    durationPopup?.remove();
}

export { addDirections, removeDirections };
